 
import React, { useState, useRef, useEffect } from 'react';
import { Checkbox } from './Checkbox';
import CloseIcon from '@mui/icons-material/Close';
import { height } from '@mui/system'
import './Styles.css';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';

export interface DropdownOptionItem {
  value: string;
  label: string;
  extraLabel?: string;
  data?: any;
}

interface DropdownProps {
  label?: string;
  options: DropdownOptionItem[];
  selectedFilterOptions?: string[];
  placeholder?: string;
  onSelect: (selectedOptions: string[]) => void;
  isMultiSelect?: boolean;
  invalid?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({ label, options, selectedFilterOptions, placeholder = "Vali", onSelect, isMultiSelect = false, invalid }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<DropdownOptionItem[]>(options);
  const [selectedOptions, setSelectedOptions] = useState<string[]>(selectedFilterOptions ?? []);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedOptions(selectedFilterOptions ?? []);
  }, [selectedFilterOptions]);

  const toggleDropdown = () => {
    if (!isOpen) {
        setFilteredOptions(
          
            options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    }
    setIsOpen(true)
};

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value;
    setSearchTerm(searchValue);
    setFilteredOptions(
      options.filter((option) => option.label.toLowerCase().includes(searchValue.toLowerCase()))
    );
  };

  const handleOptionClick = (option: string) => {
    if (isMultiSelect) {
        let newOptions = []
      if (selectedOptions.includes(option)) {
        newOptions = selectedOptions.filter((selected) => selected !== option)
      } else {
        newOptions = [...selectedOptions, option]
      }
      setSelectedOptions([...newOptions]);
      onSelect(newOptions);
    } else {
        let newOptions = []
        if (!selectedOptions.includes(option)) {
            newOptions.push(option);
            setSearchTerm("");
        }
        setSelectedOptions([...newOptions]);
        onSelect(newOptions);
        setIsOpen(false);  // Close dropdown on single selection
    }
  };

  // Close dropdown when clicking outside or when page loses focus
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        if (searchTerm == "") {
            setFilteredOptions(options);
        }
      }
    };

    const handleBlurOutside = () => {
      setIsOpen(false);
      if (searchTerm == "") {
            setFilteredOptions(options);
        }
    };

    // TODO: add moving up and down with the mouse in the dropdown selection

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('blur', handleBlurOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('blur', handleBlurOutside);
    };
  }, []);

  const handleCheckboxClicked = (index: number) => {
    setSelectedOptions(selectedOptions.filter((selected, inx) => inx != index));
  }

  // Close dropdown when clicking the label
  const handleLabelClick = () => {
    if (isOpen) {
      setIsOpen(false);
      if (searchTerm == "") {
        setFilteredOptions(options);
        }
    }
  };

  const clearSearchTerm = () => {
    setSearchTerm("");
    setFilteredOptions(options);
  }

  return (
    <div className="" ref={dropdownRef}>
      {label &&
      <label
      className="block text-sm font-medium text-gray-600 mb-1 cursor-pointer text-left"
      onClick={handleLabelClick}
    >
      {label}
    </label>
      }

      <div className="relative w-full">
        <div
        className={`w-full ${invalid ? "border-red-500 border-[1px]" : ""} w-full rounded-xl shadow-main px-3 py-2 bg-white flex justify-start`}
        >
                {isMultiSelect ? 
                <div className='w-[100%] flex box-content'>
                    <div className={"dropdown-selected-options-list w-[100%] flex flex-wrap gap-2"}>
                    {options.filter(itm => selectedOptions.includes(itm.value)).map((option, index) => (
                    <span
                        key={index}
                        className="bg-primary_light text-primary px-2 h-6 rounded-full text-sm cursor-pointer w-fit"
                        onClick={() => handleOptionClick(option.value)}
                    >
                        {option.label} &times;
                        
                    </span>
                    ))}
                    <div className='grow flex bg-transparent'>
                    <input
                        type="text"
                        value={searchTerm}
                        onClick={toggleDropdown}
                        onChange={handleSearchChange}
                        placeholder={placeholder}
                        className="bg-transparent select-none focus:outline-none focus:ring-0 pl-1 w-[100%] border-solid rounded-md shadow-s box-border"
                    />
                    {
                        searchTerm.length ?

                        <div className='w-[70px] text-primary' onClick={clearSearchTerm}>Tühjenda</div>

                        :
                        <></>
                    }
                    </div>
                    </div>
                    </div>
            :
            <div className=''>
                {options.filter((itm) => selectedOptions.includes(itm.value)).map((option, index) => (
                    <div
                        key={index}
                        className="text-primary px-2 py-1 text-sm cursor-pointer flex align-middle text-start w-full group rounded hover:bg-primary_light"
                        onClick={() => handleOptionClick(option.value)}
                    >
                        <div>
                        {option.label}
                        
                        </div>
                        <div className='w-4 h-4 ml-1 group-hover:text-red-500'>
                            <CloseIcon style={{height: '16px', width: '16px', marginBottom: '3px'}}/>
                        </div>
                    </div>
                ))}
            </div>
            }
        {!(selectedOptions.length && !isMultiSelect) && !isMultiSelect &&
            <div>
            <input
            type="text"
            value={searchTerm}
            onClick={toggleDropdown}
            onChange={handleSearchChange}
            placeholder={placeholder}
            className="bg-transparent border-none select-none focus:outline-none focus:ring-0 pl-1 flex-none"
            />
            {
                searchTerm.length ?

                <div className='w-[50px] flex-none'>Tühjenda</div>

                :
                <></>
            }
            </div>
        }
        </div>

        {/* Dropdown options */}
        {isOpen && (
          <div className="absolute mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 text-start overflow-y-auto z-20">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={index}
                  onClick={() => handleOptionClick(option.value)}
                  className={`px-3 py-2 cursor-pointer flex align-bottom justify-between hover:bg-primary_light ${selectedOptions.includes(option.value) ? 'bg-primary_light' : ''}`}
                >
                  <div className='flex flex-row'>
                    { isMultiSelect &&
                        <div className='pr-2'>
                            <Checkbox checked={selectedOptions.includes(option.value)} onChange={() => handleCheckboxClicked(index)} />
                        </div>
                    }
                    {option.label}
                  </div>
                  {option.extraLabel &&
                  <div className='text-green-600'>
                    {option.extraLabel}
                  </div>}
                    
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">Ei leitud tulemusi</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dropdown;